import React, { useState } from 'react';
import { Layout, Menu, Typography, Avatar } from 'antd';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome,
  faCog,
  faFileImport,
  faProjectDiagram,
  faDownload,
  faDatabase,
  faDollarSign,
  faChartBar,
  faPhone,
  faBolt,
  faAngleLeft,
  faAngleRight,
  faHeart,
  faUser,
  faChevronUp,
  faSignOutAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from './contexts/AuthContext';

const { Content, Sider } = Layout;
const { Title } = Typography;

// Navigation component that uses current location
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false); // Start expanded

  // Determine which submenu should be open based on current route
  const commissionPaths = ['/settings', '/import', '/saved-data', '/match', '/commission-tracker', '/commission-reports', '/export'];
  const analyticsPaths = ['/call-volume', '/recording-management', '/call-quality', '/analytics-settings'];
  
  const getDefaultOpenKeys = () => {
    if (commissionPaths.includes(location.pathname)) return ['sub-commission'];
    if (analyticsPaths.includes(location.pathname)) return ['sub-analytics'];
    return [];
  };

  const [openKeys, setOpenKeys] = useState(getDefaultOpenKeys);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMenuClick = ({ key }) => {
    if (key === 'agent-zero') return; // external link handled by onClick
    navigate(key);
  };

  // Hover state for user action flyout
  const [showUserFlyout, setShowUserFlyout] = useState(false);
  let flyoutHideTimer = null;

  const handleUserMouseEnter = () => {
    if (flyoutHideTimer) {
      clearTimeout(flyoutHideTimer);
      flyoutHideTimer = null;
    }
    setShowUserFlyout(true);
  };

  const handleUserMouseLeave = () => {
    flyoutHideTimer = setTimeout(() => setShowUserFlyout(false), 200);
  };

  const menuItems = [
    { key: '/', icon: <FontAwesomeIcon icon={faHome} />, label: 'Home' },
    {
      key: 'sub-commission',
      icon: <FontAwesomeIcon icon={faDollarSign} />,
      label: 'Commission Tracker',
      children: [
        { key: '/settings', icon: <FontAwesomeIcon icon={faCog} />, label: 'Settings' },
        { key: '/import', icon: <FontAwesomeIcon icon={faFileImport} />, label: 'Import Data' },
        { key: '/saved-data', icon: <FontAwesomeIcon icon={faDatabase} />, label: 'Saved Data' },
        { key: '/match', icon: <FontAwesomeIcon icon={faProjectDiagram} />, label: 'Match Dashboard' },
        { key: '/commission-tracker', icon: <FontAwesomeIcon icon={faDollarSign} />, label: 'Commission Tracker' },
        { key: '/commission-reports', icon: <FontAwesomeIcon icon={faChartBar} />, label: 'Commission Reports' },
        { key: '/export', icon: <FontAwesomeIcon icon={faDownload} />, label: 'Export' },
      ]
    },
    {
      key: 'sub-analytics',
      icon: <FontAwesomeIcon icon={faPhone} />,
      label: 'Google Analytics',
      children: [
        { key: '/call-volume', icon: <FontAwesomeIcon icon={faChartBar} />, label: 'Call Volume' },
        { key: '/recording-management', icon: <FontAwesomeIcon icon={faDatabase} />, label: 'Recording Management' },
        { key: '/call-quality', icon: <FontAwesomeIcon icon={faProjectDiagram} />, label: 'Call Quality' },
        { key: '/analytics-settings', icon: <FontAwesomeIcon icon={faCog} />, label: 'Settings' },
      ]
    },
    { key: 'agent-zero', icon: <FontAwesomeIcon icon={faBolt} />, label: 'Agent Zero', onClick: () => window.open('https://agentzero.us', '_blank') }
  ];

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        theme="light"
        width={250}
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          position: 'relative',
          height: '100vh',
          overflow: 'hidden'
        }}
        trigger={null}
      >
        <div style={{ 
          padding: '16px', 
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Title 
            level={4} 
            style={{ 
              margin: 0, 
              color: '#1890ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 8
            }}
          >
            {collapsed ? 'FF' : 'Family First'}
            <FontAwesomeIcon 
              icon={faHeart} 
              className="heartbeat-heart" 
              style={{ color: '#ff4d4f', fontSize: collapsed ? 14 : 16 }} 
            />
          </Title>
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          openKeys={collapsed ? [] : openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ 
            borderRight: 0, 
            paddingTop: 12, 
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingBottom: 16
          }}
        />
        
        <div style={{ 
          position: 'absolute',
          bottom: 48,
          left: 0,
          right: 0,
          padding: '12px', 
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
          zIndex: 2
        }}
        onMouseEnter={handleUserMouseEnter}
        onMouseLeave={handleUserMouseLeave}
        >
          <div style={{ 
            cursor: 'default',
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            background: '#f5f5f5',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            transition: 'all 0.2s',
            gap: '12px',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#e6f7ff';
            e.currentTarget.style.borderColor = '#1890ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f5f5f5';
            e.currentTarget.style.borderColor = '#e0e0e0';
          }}>
            <Avatar icon={<FontAwesomeIcon icon={faUser} />} style={{ backgroundColor: '#1890ff' }} />
            {!collapsed && (
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ 
                  color: '#001529', 
                  fontWeight: '500',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {user?.fullName || user?.email || 'User'}
                </div>
                <div style={{ 
                  color: '#8c8c8c', 
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  Administrator
                </div>
              </div>
            )}
            {!collapsed && <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: '12px', color: '#8c8c8c' }} />}

            {showUserFlyout && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                marginBottom: 8,
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                padding: '8px 0',
                minWidth: 200,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                zIndex: 10
              }}
              onMouseEnter={handleUserMouseEnter}
              onMouseLeave={handleUserMouseLeave}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '4px 12px',
                  fontWeight: 500,
                  fontSize: 14,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Avatar size="small" icon={<FontAwesomeIcon icon={faUser} />} style={{ backgroundColor: '#1890ff' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user?.fullName || user?.email || 'User'}
                  </span>
                </div>
                <div
                  onClick={handleLogout}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 12px',
                    borderRadius: 6,
                    transition: 'background .2s',
                    fontSize: 14
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e6f7ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div 
          onClick={() => setCollapsed(!collapsed)}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: 14,
            fontWeight: 500
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
        >
          {collapsed ? (
            <FontAwesomeIcon icon={faAngleRight} />
          ) : (
            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
              <FontAwesomeIcon icon={faAngleLeft} />
              <span style={{ fontSize:12, color:'#595959' }}>Collapse</span>
            </span>
          )}
        </div>
        
        <style jsx="true">{`
          @keyframes heartbeat {
            0%, 100% { transform: scale(1); }
            20% { transform: scale(1.15); }
            35% { transform: scale(0.95); }
            50% { transform: scale(1.10); }
            65% { transform: scale(0.97); }
            80% { transform: scale(1.05); }
          }
          .heartbeat-heart {
            animation: heartbeat 3.2s ease-in-out infinite;
            transform-origin: center;
          }
          /* Make collapsed menu items perfect squares */
          .ant-layout-sider-collapsed .ant-menu-item,
          .ant-layout-sider-collapsed .ant-menu-submenu-title {
            height: 48px !important;
            line-height: 48px !important;
            padding: 0 !important;
            padding-inline: 0 !important;
            margin: 8px auto !important;
            width: 48px !important;
            border-radius: 8px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          /* Remove all padding and center everything in collapsed mode */
          .ant-layout-sider-collapsed .ant-menu-item .ant-menu-title-content,
          .ant-layout-sider-collapsed .ant-menu-submenu-title .ant-menu-title-content {
            display: none !important;
          }
          
          /* Center the icon wrapper span in collapsed mode */
          .ant-layout-sider-collapsed .ant-menu-item > span.ant-menu-item-icon,
          .ant-layout-sider-collapsed .ant-menu-submenu-title > span.ant-menu-item-icon {
            width: 100% !important;
            margin: 0 auto !important;
            margin-inline-start: 0 !important;
            margin-inline-end: 0 !important;
            text-align: center !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          /* Target all spans in collapsed mode */
          .ant-layout-sider-collapsed .ant-menu-item > span,
          .ant-layout-sider-collapsed .ant-menu-submenu-title > span {
            margin-inline-start: 0 !important;
            margin-inline-end: 0 !important;
          }
          
          /* Expanded menu items styling */
          .ant-layout-sider:not(.ant-layout-sider-collapsed) .ant-menu-item,
          .ant-layout-sider:not(.ant-layout-sider-collapsed) .ant-menu-submenu-title {
            border-radius: 8px !important;
            margin: 4px 8px !important;
            width: calc(100% - 16px) !important;
            height: 48px !important;
            line-height: 48px !important;
            font-size: 13px !important;
          }
          /* Submenu children styling */
          .ant-menu-sub .ant-menu-item {
            height: 40px !important;
            line-height: 40px !important;
            font-size: 13px !important;
            margin: 2px 8px !important;
            width: calc(100% - 16px) !important;
            border-radius: 8px !important;
          }
          
          /* Selected state - perfect square when collapsed */
          .ant-layout-sider-collapsed .ant-menu-item-selected {
            background-color: #e6f7ff !important;
            border-radius: 8px !important;
            width: 48px !important;
            height: 48px !important;
          }
          
          /* Selected state - normal when expanded */
          .ant-layout-sider:not(.ant-layout-sider-collapsed) .ant-menu-item-selected {
            background-color: #e6f7ff !important;
            border-radius: 8px !important;
          }
          
          /* Hover states */
          .ant-menu-item:hover,
          .ant-menu-submenu-title:hover {
            background-color: #f5f5f5 !important;
            border-radius: 8px !important;
          }
          
          /* Icon sizing for collapsed mode */
          .ant-layout-sider-collapsed .ant-menu-item svg,
          .ant-layout-sider-collapsed .ant-menu-submenu-title svg {
            font-size: 20px !important;
            width: 20px !important;
            height: 20px !important;
          }
          
          /* Icon sizing for expanded mode */
          .ant-layout-sider:not(.ant-layout-sider-collapsed) .ant-menu-item svg,
          .ant-layout-sider:not(.ant-layout-sider-collapsed) .ant-menu-submenu-title svg {
            font-size: 16px;
            width: 16px !important;
            height: 16px !important;
          }
          
          /* Submenu arrow styling */
          .ant-menu-submenu-arrow {
            color: #8c8c8c !important;
          }
        `}</style>
      </Sider>
      
      <Layout style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Content style={{ padding: '24px 24px 48px', overflow: 'auto', flex: 1, background: '#fafafa' }}>
          <div style={{ maxWidth: 1600, margin: '0 auto' }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;
